import React, { useRef, useEffect, useState } from "react";
import { MAX_GUESSES, ITEMS_PER_DAY } from "../utils/gameHelpers";
import GameProgress from "./GameProgress";

// A phone (a finger, not a mouse): the keyboard covers half the screen, so it isn't brought up by itself.
const onPhone = () => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

/**
 * Whether a phone's keyboard is up: the part of the page that can be seen is much shorter than the page. While it
 * is, the page is told (html.keyboard) so the game can draw itself small, the picture and the guess box both above
 * the keyboard, and how tall the part that can be seen is (--seen).
 */
function useKeyboard() {
  useEffect(() => {
    const view = window.visualViewport;
    if (!view) return undefined;
    const look = () => {
      const up = view.height < window.innerHeight * 0.75;
      document.documentElement.classList.toggle("keyboard", up);
      document.documentElement.style.setProperty("--seen", `${Math.round(view.height)}px`);
    };
    look();
    view.addEventListener("resize", look);
    return () => {
      view.removeEventListener("resize", look);
      document.documentElement.classList.remove("keyboard");
    };
  }, []);
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

function TagTooltip({ children, text }) {
  const [show, setShow] = React.useState(false);
  return (
    <span
      className="tag-tooltip-wrapper"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && <span className="tag-tooltip">{text}</span>}
    </span>
  );
}

export default function GameBoard({
  currentItem,
  currentSlot,
  currentState,
  dailyItems,
  slotStates,
  hardMode,
  pop,
  shake,
  guess,
  allDone,
  onSlotClick,
  imageLoaded,
  onImageLoad,
  onGuessChange,
  onSubmit,
  onAdvance,
}) {
  const inputRef = useRef(null);
  const zoomStageRef = useRef(null);
  const dragStateRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [isDraggingZoom, setIsDraggingZoom] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const remaining = MAX_GUESSES - currentState.guesses.length;
  useKeyboard();

  const targetPrice = (currentItem.is_weighted && currentItem.estimated_price != null)
    ? currentItem.estimated_price
    : currentItem.price;

  const clampScale = (value) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));

  const centerZoomImage = () => {
    setZoomScale(1);
    setZoomOffset({ x: 0, y: 0 });
    dragStateRef.current.active = false;
    setIsDraggingZoom(false);
  };

  const changeZoom = (delta) => {
    setZoomScale((prev) => {
      const next = clampScale(prev + delta);
      if (next === MIN_ZOOM) {
        setZoomOffset({ x: 0, y: 0 });
      }
      return next;
    });
  };

  // Keep focus on the input after submit and when switching slots. Not on a phone: there the keyboard would cover the
  // new item before it's been seen, so the box is tapped when ready (and keeps the keyboard up after each guess).
  useEffect(() => {
    if (currentState.status === "playing" && !onPhone()) {
      inputRef.current?.focus();
    }
    setDescExpanded(false);
  }, [currentSlot, currentState.guesses.length, currentState.status]);

  useEffect(() => {
    if (!zoomOpen) return;

    const onKeyDown = (e) => {
      const applyZoomDelta = (delta) => {
        setZoomScale((prev) => {
          const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta));
          if (next === MIN_ZOOM) {
            setZoomOffset({ x: 0, y: 0 });
          }
          return next;
        });
      };

      if (e.key === "Escape") {
        setZoomOpen(false);
        return;
      }

      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        applyZoomDelta(ZOOM_STEP);
        return;
      }

      if (e.key === "-") {
        e.preventDefault();
        applyZoomDelta(-ZOOM_STEP);
        return;
      }

      if (e.key === "0") {
        e.preventDefault();
        setZoomScale(1);
        setZoomOffset({ x: 0, y: 0 });
        dragStateRef.current.active = false;
        setIsDraggingZoom(false);
        return;
      }

      if (e.key === "ArrowLeft") {
        setZoomOffset((prev) => ({ ...prev, x: prev.x + 30 }));
      } else if (e.key === "ArrowRight") {
        setZoomOffset((prev) => ({ ...prev, x: prev.x - 30 }));
      } else if (e.key === "ArrowUp") {
        setZoomOffset((prev) => ({ ...prev, y: prev.y + 30 }));
      } else if (e.key === "ArrowDown") {
        setZoomOffset((prev) => ({ ...prev, y: prev.y - 30 }));
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [zoomOpen]);

  useEffect(() => {
    if (!zoomOpen) {
      centerZoomImage();
    }
  }, [zoomOpen]);

  const handleZoomWheel = (e) => {
    if (!zoomOpen) return;
    e.preventDefault();
    const direction = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setZoomScale((prev) => {
      const next = clampScale(prev + direction);
      if (next === MIN_ZOOM) {
        setZoomOffset({ x: 0, y: 0 });
      }
      return next;
    });
  };

  const handleZoomMouseDown = (e) => {
    if (zoomScale <= 1) return;
    e.preventDefault();
    dragStateRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: zoomOffset.x,
      originY: zoomOffset.y,
    };
    setIsDraggingZoom(true);
  };

  const handleZoomMouseMove = (e) => {
    if (!dragStateRef.current.active) return;
    const dx = e.clientX - dragStateRef.current.startX;
    const dy = e.clientY - dragStateRef.current.startY;
    setZoomOffset({
      x: dragStateRef.current.originX + dx,
      y: dragStateRef.current.originY + dy,
    });
  };

  const handleZoomMouseUp = () => {
    if (!dragStateRef.current.active) return;
    dragStateRef.current.active = false;
    setIsDraggingZoom(false);
  };

  const handleZoomDoubleClick = () => {
    setZoomScale((prev) => {
      if (prev < 2.5) return 2.5;
      if (prev < 4) return 4;
      setZoomOffset({ x: 0, y: 0 });
      return 1;
    });
  };

  return (
    <>
      <div className="game-layout">
        <div className="game-layout-item">
          <GameProgress
            dailyItems={dailyItems}
            slotStates={slotStates}
            currentSlot={currentSlot}
            onSlotClick={onSlotClick}
          />
          <div className="item-display">
            <p className="round-label">Item {currentSlot} of {ITEMS_PER_DAY}</p>
            <div className="image-container">
              {!imageLoaded[currentSlot] && <div className="skeleton-image" />}
              <button
                type="button"
                className="image-zoom-trigger"
                onClick={() => setZoomOpen(true)}
                disabled={!imageLoaded[currentSlot]}
                aria-label={`Zoom image for ${currentItem.name}`}
              >
                <img
                  src={currentItem.image_url}
                  alt={currentItem.name}
                  onLoad={() => onImageLoad(currentSlot)}
                  className={imageLoaded[currentSlot] ? "loaded" : "loading"}
                />
              </button>
            </div>
            {imageLoaded[currentSlot] && <p className="image-zoom-hint">Tap image to zoom</p>}
            <p className="item-name">
              Guess the price of: <strong>{currentItem.name}</strong>
            </p>
            {(currentItem.brand || currentItem.size || currentItem.subcategory) && (
              <div className="item-details">
                {currentItem.brand && currentItem.brand.trim() && <TagTooltip text="Brand"><span className="item-detail-tag">{currentItem.brand}</span></TagTooltip>}
                {currentItem.size && currentItem.size.trim() && <TagTooltip text="Size"><span className="item-detail-tag">{currentItem.size}</span></TagTooltip>}
                {currentItem.subcategory && currentItem.subcategory.trim() && <TagTooltip text="Category"><span className="item-detail-tag">{currentItem.subcategory}</span></TagTooltip>}
              </div>
            )}
            {currentItem.is_weighted && currentItem.estimated_price != null && (
              <p className="item-extra-text item-extra-weighted">⚖️ Priced by weight — guess the <strong>total estimated price</strong></p>
            )}
            {currentItem.attributes && currentItem.attributes.filter(a => a && a.trim()).length > 0 && (
              <div className="item-extra-tags">
                {currentItem.attributes.filter(a => a && a.trim()).map((a, i) => (
                  <TagTooltip key={i} text="Attribute"><span className="item-detail-tag">{a}</span></TagTooltip>
                ))}
              </div>
            )}
            {(currentItem.description?.trim() || currentItem.ingredients?.trim() || currentItem.allergens?.trim()) && (
              <div className="item-desc-section">
                <button
                  type="button"
                  className="item-desc-toggle"
                  onClick={() => setDescExpanded((prev) => !prev)}
                  aria-expanded={descExpanded}
                >
                  Details <span className={`item-desc-arrow${descExpanded ? " expanded" : ""}`}>▾</span>
                </button>
                <div className={`item-desc-content${descExpanded ? " open" : ""}`}>
                  {currentItem.description && currentItem.description.trim() && (
                    <p className="item-extra-text">{currentItem.description}</p>
                  )}
                  {currentItem.ingredients && currentItem.ingredients.trim() && (
                    <p className="item-extra-text item-extra-ingredients"><strong>Ingredients:</strong> {currentItem.ingredients}</p>
                  )}
                  {currentItem.allergens && currentItem.allergens.trim() && (
                    <p className="item-extra-text item-extra-allergens"><strong>Allergens:</strong> {currentItem.allergens}</p>
                  )}
                </div>
              </div>
            )}
            {currentState.status !== "playing" && currentItem.is_weighted && currentItem.unit_price && currentItem.unit_price.trim() && (
              <p className="item-extra-text">{currentItem.unit_price}</p>
            )}
          </div>
        </div>

        <div className="game-layout-guess">
          <div className="game-meta">
            <p>📅 {new Date().toDateString()}</p>
            <div className={`guess-count ${pop ? "pop" : ""}`}>
              Guesses Remaining: {remaining}
            </div>
          </div>

          {currentState.status === "won" && (
            <>
              <h2 className={`result-text win ${hardMode ? "hard-mode-win" : ""}`}>
                {hardMode ? "🔥 Nailed it!" : "🎉 Correct!"} The price was ${targetPrice.toFixed(2)}
              </h2>
              <button className="next-btn" onClick={onAdvance}>
                {allDone ? "See Results" : "Next Item →"}
              </button>
            </>
          )}
          {currentState.status === "lost" && (
            <>
              <h2 className="result-text lose lose-animate">
                ✗ The price was ${targetPrice.toFixed(2)}
              </h2>
              <button className="next-btn" onClick={onAdvance}>
                {allDone ? "See Results" : "Next Item →"}
              </button>
            </>
          )}

          {currentState.status === "playing" && (
            <form
              onSubmit={(e) => { onSubmit(e); inputRef.current?.focus(); }}
              className={`guess-form ${shake ? "shake" : ""}`}
            >
              <span className="dollar-sign">$</span>
              <input
                ref={inputRef}
                type="text"
                value={guess}
                onChange={onGuessChange}
                placeholder="0.00"
                inputMode="decimal"
                className="guess-input"
                autoFocus={!onPhone()}
                // The keyboard coming up: the box kept in sight above it.
                onFocus={() => setTimeout(() => inputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
              />
              <button type="submit" className="submit-btn">Submit</button>
            </form>
          )}

          <div className="guesses">
        {Array.from({ length: MAX_GUESSES }).map((_, index) => {
          const g = currentState.guesses[index];
          const isVisible = index < currentState.guesses.length;
          const isLatest = index === currentState.guesses.length - 1;

          let content = "";
          let boxClass = "guess-box";

          if (g) {
            const arrowChar =
              g.feedback === "correct"
                ? "✓"
                : g.feedback === "high" || g.feedback === "way high"
                ? "↓"
                : "↑";

            const arrowAnimClass =
              g.feedback === "correct"
                ? "arrow-correct"
                : g.feedback === "high" || g.feedback === "way high"
                ? "arrow-down"
                : "arrow-up";

            content = (
              <>
                ${g.guess.toFixed(2)}
                {!hardMode && (
                  <span className={`arrow-fix arrow-animated ${isLatest ? arrowAnimClass : ""}`}>
                    {arrowChar}
                  </span>
                )}
              </>
            );

            boxClass += ` ${g.feedback?.replace(" ", "-") || ""}`;
          }

          return (
            <div
              key={index}
              className={`${boxClass} ${isVisible ? "fade-in" : ""}`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {g ? content : ""}
            </div>
          );
        })}
      </div>
        </div>
      </div>

      {zoomOpen && (
        <div className="image-zoom-overlay" onClick={() => setZoomOpen(false)} role="presentation">
          <div
            className="image-zoom-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Zoomed image for ${currentItem.name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-zoom-toolbar">
              <button type="button" className="image-zoom-btn" onClick={() => changeZoom(-ZOOM_STEP)} aria-label="Zoom out">−</button>
              <span className="image-zoom-level">{Math.round(zoomScale * 100)}%</span>
              <button type="button" className="image-zoom-btn" onClick={() => changeZoom(ZOOM_STEP)} aria-label="Zoom in">+</button>
              <button type="button" className="image-zoom-reset" onClick={centerZoomImage}>Reset</button>
            </div>
            <button
              type="button"
              className="image-zoom-close"
              onClick={() => setZoomOpen(false)}
              aria-label="Close zoomed image"
            >
              ✕
            </button>
            <div
              ref={zoomStageRef}
              className="image-zoom-stage"
              onWheel={handleZoomWheel}
              onMouseMove={handleZoomMouseMove}
              onMouseUp={handleZoomMouseUp}
              onMouseLeave={handleZoomMouseUp}
            >
              <img
                src={currentItem.image_url}
                alt={currentItem.name}
                className={`image-zoom-full ${zoomScale > 1 ? "zoomed" : ""} ${isDraggingZoom ? "dragging" : ""}`}
                style={{ transform: `translate(${zoomOffset.x}px, ${zoomOffset.y}px) scale(${zoomScale})` }}
                onMouseDown={handleZoomMouseDown}
                onDoubleClick={handleZoomDoubleClick}
                draggable={false}
              />
            </div>
            <p className="image-zoom-help">Scroll to zoom • Drag to pan • Double-click for quick zoom</p>
          </div>
        </div>
      )}
    </>
  );
}
